import { DefaultValues, FieldValues, UseFormReturn, useForm } from "react-hook-form"
import React, { ReactNode } from "react"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { handleFormValidationErrors } from "app/core/components/error-handlers/handleFormValidationError"
import { handleFormSubmissionError } from "app/core/components/error-handlers/handleFormSubmissionError"
import { Toast, useToast } from "app/core/components/page-layout/Toast"

export type FormProps<TFieldValues extends FieldValues = FieldValues> = {
  controls: (
    form: Pick<UseFormReturn<any, any>, "control" | "formState" | "register" | "setValue">,
  ) => ReactNode
  submitText?: string
  schema: z.Schema<any, any>
  submitHandler: (data: TFieldValues) => Promise<void>
  defaultValues?: DefaultValues<TFieldValues>
  alternativeOption?: () => void
  alternativeOptionText?: string
}

export const Form = <TFieldValues extends FieldValues = FieldValues>({
  controls,
  schema,
  defaultValues,
  submitHandler,
}: FormProps<TFieldValues>) => {
  const { control, handleSubmit, formState, register, setValue } = useForm({
    defaultValues,
    resolver: async (values, context, options) => zodResolver(schema)(values, context, options),
  })
  const toast = useToast()

  return (
    <>
      <Toast {...toast.props} />
      <form
        onSubmit={async (event) => {
          try {
            await handleSubmit(submitHandler, (errors) => {
              handleFormValidationErrors(errors, toast)
            })(event)
          } catch (err) {
            handleFormSubmissionError(err, toast)
          }
        }}
      >
        <div className="vstack gap-3 my-3">
          {/* Form fields supplied as children are rendered here */}
          {controls({ control, formState, register, setValue })}
        </div>
      </form>
    </>
  )
}
